import { Utils } from '@genial/utils';

import {
  TransactionFlowCurrentAccount,
  TransactionFlowFixedIncome,
  TransactionFlowFunds,
  TransactionFlowHistoricalSearch,
  TransactionFlowPublicOffer,
  TransactionFlowRequiredMargin,
  TransactionFlowTreasury,
  TransactionFlowVariableIncome,
} from './types';

export const normalizeTransactionFlowFundsResponse = (data: any): TransactionFlowFunds => {
  return {
    totalRedemption: data?.totalResgate || 0,
    totalApplication: data?.totalAplicacao || 0,
    details: (data?.detalhe || []).map((item: any) => ({
      requestDate: Utils.Mask.DateFormat(item?.dataSolicitacao, 'dd/MM/yyyy HH:mm:ss') || '--',
      advisorId: item?.idAssessor || 0,
      clientId: item?.idCliente || 0,
      accountId: item?.idConta || 0,
      clientDocument: item?.documentoCliente || '--',
      managerId: item?.idGestor || 0,
      managerName: item?.nomeGestor || null,
      productName: item?.nomeProduto || '--',
      advisorName: item?.nomeAssessor || '--',
      clientName: item?.nomeCliente || '--',
      status: item?.status || '--',
      movementType: item?.tipoMovimento || '--',
      movementValue: Utils.Mask.Money(item?.valorMovimento) || '--',
    })),
  };
};

export const normalizeTransactionFlowTreasuryResponse = (data: any): TransactionFlowTreasury => {
  return {
    totalRedemption: data?.totalVenda || 0,
    totalApplication: data?.totalCompra || 0,
    details: (data?.detalhe || []).map((item: any) => ({
      movementDate: Utils.Mask.DateFormat(item?.dataMovimento, 'dd/MM/yyyy HH:mm:ss') || '--',
      movementType: item?.tipoMovimento || '--',
      clientDocument: item?.cpfCnpj || '--',
      accountId: item?.idConta || 0,
      clientName: item?.nomeCliente || '--',
      issuerSrc: item?.emissorSrc || '--',
      issuerName: item?.nomeEmissor || '--',
      bondType: item?.tipoTitulo || '--',
      rateValue: item?.valorTaxa ? `${item.valorTaxa}%` : '--',
      maturityDate: Utils.Mask.DateFormat(item?.dataVencimento, 'dd/MM/yyyy') || '--',
      financial: item?.financeiro ? Utils.Mask.Money(item.financeiro) : '--',
      statusDescription: item?.descricaoStatus || '--',
    })),
  };
};

export const normalizeTransactionFlowVariableIncomeResponse = (
  data: any
): TransactionFlowVariableIncome => {
  return {
    totalPurchase: data?.totalCompra || 0,
    totalSale: data?.totalVenda || 0,
    details: (data?.detalhe || []).map((item: any) => ({
      requestDate: Utils.Mask.DateFormat(item?.dataSolicitacao, 'dd/MM/yyyy HH:mm:ss') || '--',
      movementType: item?.tipoMovimento || '--',
      clientDocument: item?.documentoCliente || '--',
      accountId: item?.idConta || 0,
      clientName: item?.nomeCliente || '--',
      productName: item?.nomeProduto || '--',
      maturityDate: Utils.Mask.DateFormat(item?.dataVencimento, 'dd/MM/yyyy') || '--',
      movementValue: Utils.Mask.Money(item?.valorMovimento) || '--',
      status: item?.situacao || '--',
    })),
  };
};

export const normalizeTransactionFlowFixedIncomeResponse = (
  data: any
): TransactionFlowFixedIncome => {
  return {
    totalRedemption: data?.totalResgate || 0,
    totalApplication: data?.totalAplicacao || 0,
    details: (data?.detalhe || []).map((item: any) => ({
      advisorCode: item?.codigoAssessor || 0,
      accountCode: item?.codigoConta || 0,
      movementDate: Utils.Mask.DateFormat(item?.dataMovimento, 'dd/MM/yyyy') || '--',
      requestDate: Utils.Mask.DateFormat(item?.dataSolicitacao, 'dd/MM/yyyy HH:mm:ss') || '--',
      maturityDate: Utils.Mask.DateFormat(item?.dataVencimento, 'dd/MM/yyyy') || '--',
      rateDescription: item?.descricaoTaxa || '--',
      elapsedDays: item?.diasCorridos || 0,
      clientDocument: item?.documentoCliente || '--',
      cancellationDate: item?.dtCancelamento
        ? Utils.Mask.DateFormat(item.dtCancelamento, 'dd/MM/yyyy')
        : null,
      advisorId: item?.idAssessor || 0,
      bankId: item?.idBanco || 0,
      clientId: item?.idCliente || 0,
      accountId: item?.idConta || 0,
      benchmarkId: item?.idDmBenchmark || null,
      productId: item?.idProduto || 0,
      fixedIncomeId: item?.idRendaFixa || 0,
      isOrder: item?.isPedido || false,
      bankName: item?.nmBanco || null,
      benchmarkName: item?.nmDmBenchmark || null,
      advisorName: item?.nomeAssessor || '--',
      clientName: item?.nomeCliente || '--',
      issuerName: item?.nomeEmissor || '--',
      productName: item?.nomeProduto || '--',
      requestedQuantity: item?.qtdSolicitada || 0,
      status: item?.situacao || '--',
      assetType: item?.tipoAtivo || null,
      marketType: item?.tipoMercado || null,
      movementType: item?.tipoMovimento || '--',
      movementValue: Utils.Mask.Money(item?.valorMovimento) || '--',
      rateValue: item?.valorTaxa ? `${item.valorTaxa}%` : '--',
    })),
  };
};

export const normalizeTransactionFlowRequiredMarginResponse = (
  data: any
): TransactionFlowRequiredMargin => {
  return {
    totalRequired: data?.totalRequerida || 0,
    totalGuarantee: data?.totalGarantia || 0,
    details: (data?.detalhe || []).map((item: any) => ({
      clientDocument: item?.idCpfCnpj || 0,
      accountId: item?.idConta || 0,
      clientName: item?.nmCliente || '--',
      requiredMarginValue: Utils.Mask.Money(item?.vlMargemRequerida) || '--',
      totalGuaranteeValue: Utils.Mask.Money(item?.vlTotalGarantia) || '--',
      marginBalanceValue: Utils.Mask.Money(item?.vlSaldoMargem) || '--',
    })),
  };
};

export const normalizeTransactionFlowPublicOfferResponse = (
  data: any
): TransactionFlowPublicOffer => {
  return {
    totalSettled: data?.totalLiquidado || 0,
    totalReserved: data?.totalReservado || 0,
    details: (data?.detalhe || []).map((item: any) => ({
      requestDate: Utils.Mask.DateFormat(item?.dataSolicitacao, 'dd/MM/yyyy HH:mm:ss') || '--',
      offerType: item?.tipoOferta || '--',
      clientDocument: item?.documentoCliente || '--',
      accountId: item?.idConta || 0,
      clientName: item?.nomeCliente || '--',
      companyTickerDescription: item?.descricaoTickerEmpresa || '--',
      companyName: item?.nomeEmpresa || '--',
      modalityDescription: item?.descricaoModalidade || '--',
      offerValue: Utils.Mask.Money(item?.valorOferta) || '--',
      statusDescription: item?.descricaoStatus || '--',
    })),
  };
};

export const normalizeTransactionFlowCurrentAccountResponse = (
  data: any
): TransactionFlowCurrentAccount => {
  return {
    totalDeposit: data?.totalDeposito || 0,
    totalWithdrawal: data?.totalRetirada || 0,
    details: (data?.detalhe || []).map((item: any) => ({
      requestDate: Utils.Mask.DateFormat(item?.dataSolicitacao, 'dd/MM/yyyy HH:mm:ss') || '--',
      movementType: item?.tipoMovimento || '--',
      clientDocument: item?.documentoCliente || '--',
      accountId: item?.idConta || 0,
      clientName: item?.nomeCliente || '--',
      movementValue: Utils.Mask.Money(item?.valorMovimento) || '--',
      status: item?.situacao || '--',
    })),
  };
};

export const normalizeTransactionFlowHistoricalSearchResponse = (
  data: any
): TransactionFlowHistoricalSearch => {
  return {
    transaction: (data?.movimentacoes || []).map((item: any) => ({
      clientName: item?.nomeCliente || '--',
      resourceName: item?.nomeRecurso || null,
      accountId: item?.idConta || 0,
      clientDocument: item?.documentoCliente || '--',
      movementId: item?.idMovimentacao || 0,
      movementCCId: item?.idMovimentacaoCC || 0,
      requestDate: Utils.Mask.DateFormat(item?.dataSolicitacao, 'dd/MM/yyyy HH:mm:ss') || '--',
      settlementDate: Utils.Mask.DateFormat(item?.dataLiquidacao, 'dd/MM/yyyy') || '--',
      product: item?.produto || '--',
      type: item?.tipo || '--',
      marketType: item?.tipoMercado || 0,
      value: item?.valor || 0,
      requestedQuantity: item?.qtdSolicitada || 0,
      asset: item?.ativo
        ? item.ativo.length > 18
          ? `${item.ativo.substring(0, 18)}...`
          : item.ativo
        : '--',
      status: item?.status || '--',
      situation: item?.situacao || null,
      isOrder: item?.isPedido || false,
      isCancelable: item?.isPermiteCancelar || false,
      modality: item?.modalidade || null,
      clientInfo: item?.clientInfo || null,
      requestUserId: item?.idUsuarioSolicitacao || null,
      pageNumber: item?.numeroPagina || null,
    })),
    page: data?.pagina || 1,
    totalItems: data?.totalItens || 0,
  };
};
